import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { Product } from '@interfaces/models/product';

import { DataLayerInterface } from '@interfaces/models/dataLayerInterface';
import { ElasticSearch } from '@interfaces/models/elasticSearch';
import { AppCookies } from '@enums/cookies';
import Cookies from 'universal-cookie';
import { ProductSearchHeaders } from '@services/search-service';
import { FilterSources } from '@interfaces/models/filter-sources';
import { pickFiltersBySources } from '@helpers/utils/catalog/filter-sources-helpers';
import { FilterSource } from '@enums/filter-source';

export type CatalogDatalayerType = Pick<
  DataLayerInterface,
  | 'catalog_version'
  | 'index_name'
  | 'nbHits'
  | 'perso_available'
  | 'perso_created'
  | 'perso_enabled'
  | 'perso_results_nb'
  | 'perso_results_returned'
  | 'list_products'
  | 'search_query_id'
  | 'filters_details'
  | 'catalog_brand'
  | 'catalog_universe'
  | 'catalog_category'
  | 'catalog_subcategory'
  | 'nb_results'
  | 'filters_applied'
  | 'are_filters_active'
  | 'has_guided_search'
  | 'nb_guided_search_chips'
> &
  Partial<Pick<DataLayerInterface, 'source_category' | 'source_subcategory'>>;

export const pickSearchContextAttributes = (
  attributes: (keyof DataLayerInterface)[],
): Partial<CatalogDatalayerType> => {
  if (typeof window === 'undefined') {
    return {};
  }
  const dataLayer = window.tc_vars;
  if (!dataLayer) {
    return {};
  }
  return attributes.reduce((acc, attribute) => {
    acc[attribute] = dataLayer[attribute];
    return acc;
  }, {});
};

export const RESET_CATALOG_DATALAYER: Partial<DataLayerInterface> = {
  keyword: '',
  keyword_user_input: '',
  keyword_autocorrected: '',
  campaign_id: '',
  onsite_campaign_id: '',
  filtered_campaign_id: '',
  pageUrl: '',
  sorting: '',
};

export const getProductTrackingData = (product: CatalogProduct | Product) => ({
  product_id: String(product.id),
  seller_id: product.seller?.id ? product.seller?.id : '',
  product_universe: 'universeId' in product ? String(product.universeId) : '',
  product_category: 'category' in product ? String(product.category.localizedName) : '',
  product_sub_category: 'subcategory' in product ? String(product.subcategory.localizedName) : '',
  product_brand: product.brand?.name,
  product_unitprice_ati: String((product.price?.cents ?? 0) / 100 ?? ''),
  product_country: product.isoCountry ?? '',
  product_card_tag: 'isNewProduct' in product && product.isNewProduct ? 'NEW' : product.tags?.join(',') ?? '',
  currency: product.price?.currency ?? '',
});

// CMS `productType` and similar ids use hyphens; analytics property segments use underscores.
export const normalizeProductTypeForTracking = (value: string | undefined): string => (value ?? '').replace(/-/g, '_');

function slugFromBlockHeading(text: string | undefined): string {
  if (!text?.trim()) {
    return '';
  }
  return text
    .trim()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

// CMS product block: heading (e.g. h2), when present, wins over technical productType for analytics
export const getCmsProductFeedCategory = (params: {
  blockHeading?: string;
  productType?: string;
  contentTypeUid?: string;
}): string => {
  const fromHeading = slugFromBlockHeading(params.blockHeading);
  if (fromHeading) {
    return fromHeading;
  }
  return normalizeProductTypeForTracking(
    params.productType ?? (params.contentTypeUid ? `cms_${params.contentTypeUid}` : 'cms_product_catalog'),
  );
};

export const getCatalogTrackingData = () =>
  pickSearchContextAttributes([
    'keyword',
    'index_name',
    'catalog_brand',
    'catalog_universe',
    'catalog_category',
    'catalog_subcategory',
    'campaign_id',
    'cms_block_id',
    'filters_applied',
    'nb_results',
    'filters_details',
    'search_query_id',
  ]);

export const getSeasUserHeaders = (userId: string | undefined, serverAnonymousId?: string | undefined) => {
  // on the server we get the anonymousId from serverAnonymousId, on the client we can directly take the cookie value
  let anonymousIdFromCookie = '';
  if (serverAnonymousId) {
    anonymousIdFromCookie = serverAnonymousId;
  } else {
    const cookies = new Cookies();
    anonymousIdFromCookie = cookies.get<string | undefined>(AppCookies.ANONYMOUS_USER_ID_SSR) ?? '';
  }

  return {
    [ProductSearchHeaders.userId]: userId ?? '',
    [ProductSearchHeaders.deviceId]: anonymousIdFromCookie,
  };
};

export const getCatalogDatalayerEntrypoint = ({
  source_category,
  source_subcategory,
  cms_block_id,
}: {
  source_category?: string;
  source_subcategory?: string;
  cms_block_id?: string;
}) => {
  return {
    source_category: source_category ?? '',
    source_subcategory: source_subcategory ?? '',
    cms_block_id: cms_block_id ?? '',
  };
};

// catalogLinksWithoutLanguage is a system filter (URL-derived, not user-originated) but is intentionally
// included in tracking filters_details across impression / apply_filter / sorting_change events so the
// originating catalog link is preserved alongside the user-applied filter snapshot.
// URL-derived filters (UrlHash / UrlPath / UrlAnalysis) are deliberately excluded — filters that arrive
// via a shared link should not be reported as active. Recent-search URLs are tagged separately as
// FilterSource.RecentSearch so they remain tracked.
const FILTER_DETAILS_ALLOWED_SOURCES = [FilterSource.User, FilterSource.RecentSearch];

export const getFilterDetailsForTracking = (
  filters: ElasticSearch['filters'],
  filterSources?: FilterSources,
): ElasticSearch['filters'] => {
  const userFilters = filterSources
    ? pickFiltersBySources(filters, filterSources, FILTER_DETAILS_ALLOWED_SOURCES)
    : filters ?? {};
  if (filters?.catalogLinksWithoutLanguage) {
    return { ...userFilters, catalogLinksWithoutLanguage: filters.catalogLinksWithoutLanguage };
  }
  return userFilters;
};

export const getApplyFilterPayload = (
  filters: ElasticSearch['filters'],
  filters_applied: { [key: string]: string },
  filterSources?: FilterSources,
) => {
  return {
    are_filters_active: 'true',
    filters_applied: JSON.stringify(filters_applied),
    filters_details: JSON.stringify(getFilterDetailsForTracking(filters, filterSources)),
    ...pickSearchContextAttributes([
      'keyword',
      'index_name',
      'catalog_brand',
      'catalog_universe',
      'catalog_category',
      'catalog_subcategory',
      'nb_results',
      'search_query_id',
      'search_session_id',
    ]),
  };
};

export const getSortingChangePayload = (filters: ElasticSearch['filters'], filterSources?: FilterSources) => ({
  filters_details: JSON.stringify(getFilterDetailsForTracking(filters, filterSources)),
});

export const PLP_SOURCE_CATEGORY_QUERY_KEY = 'plp_source_category';

export enum PlpSourceCategory {
  guidedSearch = 'guided_search',
}
